import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Banknote, Mail, Phone } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Chips, Empty, Loading, PageHead, fmtWhen, money, useAdmin } from "./ui";
import { DataTools, RowDelete } from "./DataTools";

const STATUSES = ["tentative", "awaiting_payment", "confirmed", "needs_review", "completed", "cancelled", "bumped"];
const label = (s) => s.replace("_", " ");

const PaymentForm = ({ b, onDone }) => {
  const [amount, setAmount] = useState(b.deposit || "");
  const [method, setMethod] = useState("cash");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/bookings/${b.id}/payment`, { amount: Number(amount), method });
      toast.success("Payment recorded — booking confirmed");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="ad-inline-form" data-testid="record-payment-form">
      <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" data-testid="record-payment-amount" />
      <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="record-payment-method">
        <option value="cash">Cash</option><option value="bank_transfer">Bank transfer</option><option value="card_in_store">Card in store</option><option value="other">Other</option>
      </select>
      <button className="btn gold" disabled={busy || !amount} onClick={save} data-testid="record-payment-save">Save</button>
    </div>
  );
};

const BookingCard = ({ b, refresh }) => {
  const [paying, setPaying] = useState(false);
  const [notes, setNotes] = useState(b.admin_notes || "");
  const patch = async (body) => {
    try { await api.patch(`/admin/bookings/${b.id}`, body); refresh(); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <article className={`req st-${b.status}`} data-testid={`booking-card-${b.id}`}>
      <div className="req-top">
        <div><h3 data-testid="booking-card-name">{b.name}</h3><p className="req-meta">Received {fmtWhen(b.created_at)} · {b.booking_type}</p></div>
        <span className={`pill st-pill-${b.status}`} data-testid="booking-card-status">{label(b.status)}</span>
      </div>
      <dl>
        <dt>Treatment</dt><dd>{b.treatment}</dd>
        <dt>When</dt><dd data-testid="booking-card-when">{b.preferred_date} · {b.time_label}</dd>
        <dt>Price</dt><dd>{b.price || "—"} {b.duration && `· ${b.duration}`}</dd>
        {b.deposit > 0 && <><dt>Deposit</dt><dd>{money(b.deposit)} · {b.payment_status}</dd></>}
        <dt>Contact</dt><dd>{b.phone}<br />{b.email}</dd>
        {b.notes && <><dt>Notes</dt><dd>{b.notes}</dd></>}
      </dl>
      <div className="ad-booking-ctrl">
        <select value={b.status} onChange={(e) => patch({ status: e.target.value })} data-testid="booking-status-select">
          {STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
        </select>
        <label className="ad-check"><input type="checkbox" checked={b.handled} onChange={(e) => patch({ handled: e.target.checked })} data-testid="booking-handled-checkbox" /> Handled</label>
      </div>
      <textarea className="ad-notes" placeholder="Private staff notes" value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => notes !== (b.admin_notes || "") && patch({ admin_notes: notes })} data-testid="booking-admin-notes" />
      <div className="req-actions">
        <a className="btn light" href={`tel:${b.phone}`} data-testid="booking-call-button"><Phone size={15} /> Call</a>
        <a className="btn light" href={`mailto:${b.email}`} data-testid="booking-email-button"><Mail size={15} /> Email</a>
        {b.payment_status !== "paid" && <button className="btn pink" onClick={() => setPaying(!paying)} data-testid="booking-record-payment-button"><Banknote size={15} /> Record deposit</button>}
        <RowDelete url={`/admin/bookings/${b.id}`} name={`${b.name}'s booking on ${b.preferred_date}`} onDone={refresh} testId="booking-delete-button" />
      </div>
      {paying && <PaymentForm b={b} onDone={() => { setPaying(false); refresh(); }} />}
    </article>
  );
};

export default function Bookings() {
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const { data } = useAdmin("bookings", "/admin/bookings");
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin"] });
  const shown = (data || []).filter((b) => (filter === "all" || (filter === "open" ? !b.handled : b.status === filter))
    && (!q || `${b.name} ${b.email} ${b.phone} ${b.treatment}`.toLowerCase().includes(q.toLowerCase())));
  return (
    <div data-testid="admin-bookings">
      <PageHead eyebrow={`${(data || []).filter((b) => !b.handled).length} need attention`} title="Bookings" />
      <DataTools section="bookings" label="bookings" exportUrl="/admin/bookings/export" filename="waxmoi-bookings.xlsx" onPurged={refresh} />
      <input className="ad-search" placeholder="Search name, email, phone or treatment" value={q} onChange={(e) => setQ(e.target.value)} data-testid="bookings-search" />
      <Chips options={[["all", "All"], ["open", "Not handled"], ["confirmed", "Confirmed"], ["tentative", "Tentative"], ["awaiting_payment", "Awaiting deposit"], ["bumped", "Bumped"], ["cancelled", "Cancelled"]]} value={filter} onChange={setFilter} testPrefix="bookings-filter" />
      {!data ? <Loading /> : shown.length === 0 ? <Empty>No bookings here yet.</Empty> : (
        <div className="req-list ad-cards">{shown.map((b) => <BookingCard key={b.id} b={b} refresh={refresh} />)}</div>
      )}
    </div>
  );
}
